import { PreviewCart } from "../cart/preview-cart";
import { ProductVariants } from "../product/product-variants";
import { GeneralOverlay } from "../general/general-overlay";
import { debounce } from "@app/scripts/utils/debounce";
import Swiper, { Pagination } from 'swiper';

class ProductCard extends HTMLElement {
    private hoverCardContainer: HTMLElement | null = null;
    private isHoverActive: boolean = false;
    private swiper: Swiper | null = null;
    private quickAddTrigger: HTMLElement | null = null;
    private escapeHandler: ((e: KeyboardEvent) => void) | null = null;
    private focusTrapHandler: ((e: KeyboardEvent) => void) | null = null;
    // -- SG 20260707 Bound focus handlers for desktop keyboard hover-reveal parity
    private handleFocusIn = (e: FocusEvent) => {
        if (window.innerWidth <= 750) return;
        if (this.querySelector('.product-card__quick-add-panel.open')) return;
        if (this.classList.contains('has-hover-content')) return;
        this.isHoverActive = true;
        this.createHoverCard();
    };
    private handleFocusOut = (e: FocusEvent) => {
        if (window.innerWidth <= 750) return;
        if (this.contains(e.relatedTarget as Node)) return;
        if (this.querySelector('.product-card__quick-add-panel.open')) return;
        this.isHoverActive = false;
        this.removeHoverCard();
    };
    private onMouseLeaveClosePanel = () => {
        // -- SG 20260708 Only keep the panel open on mouseleave when focus inside was reached
        // via keyboard (focus-visible). A mouse click focuses the <button> trigger/close control,
        // so an activeElement-only guard wrongly suppressed close for mouse users.
        if (this.hasVisibleFocusInside()) return;
        this.closeQuickAddPanel();
    };

    // -- SG 20260708 True only when a descendant has keyboard-style focus (:focus-visible);
    // falls back to any focus inside if the browser lacks :focus-visible support.
    private hasVisibleFocusInside(): boolean {
        try {
            return !!this.querySelector(":focus-visible");
        } catch {
            return this.contains(document.activeElement);
        }
    }
    
    constructor() {
        super();
        this.setupHoverEffects();
        this.bindEvents();
        this.initMobileSwiper();
    }

    // Initialize Swiper for mobile only
    private initMobileSwiper() {
        if (window.innerWidth <= 750) {
            const swiperContainer = this.querySelector('.product-card__swiper');
            if (swiperContainer) {
                const isInSplide = this.closest('.splide__slide') !== null;
                const slideCount = swiperContainer.querySelectorAll('.swiper-slide').length;
                const paginationEl = swiperContainer.querySelector('.product-card__swiper-pagination');
                const enablePagination = slideCount > 1 && paginationEl;

                const swiperConfig: ConstructorParameters<typeof Swiper>[1] = {
                    slidesPerView: 1,
                    spaceBetween: 0,
                    loop: true,
                    allowTouchMove: !isInSplide,
                    touchRatio: isInSplide ? 0 : 1,
                    touchAngle: 45,
                    grabCursor: !isInSplide,
                };

                if (enablePagination) {
                    swiperConfig.modules = [Pagination];
                    swiperConfig.pagination = {
                        el: paginationEl as HTMLElement,
                    };
                }

                this.swiper = new Swiper(swiperContainer as HTMLElement, swiperConfig);
            }
        }

        // Handle resize events to initialize/destroy swiper
        window.addEventListener('resize', debounce(() => {
            if (window.innerWidth <= 750) {
                if (!this.swiper) {
                    this.initMobileSwiper();
                }
            } else {
                if (this.swiper) {
                    this.swiper.destroy();
                    this.swiper = null;
                }
            }
        }, 300));
    }

    bindEvents() {
        if (this.hasAttribute('data-coming-soon')) return;

        const quickAddElements = this.querySelectorAll(".product-card__quick-add");
        quickAddElements.forEach((element: any) => {
            element?.addEventListener("click", this.openQuickAddPanel.bind(this));
        })

        const addToCartButton = this.querySelector(".product__cta-button");
        addToCartButton?.addEventListener("click", this.onAddToCartClick.bind(this));

        if (window.matchMedia('(hover: hover)').matches) {
            this.addEventListener("mouseleave", this.onMouseLeaveClosePanel);
        }

        const productVariantInputs = this.querySelectorAll("product-variants input");
        const productVariants = new ProductVariants();
        productVariantInputs.forEach((input) => {
            const variantId = input.getAttribute("data-id") as string;

            input.addEventListener("change", () => {
                productVariants.onVariantSelect(variantId, this)
            })
        })

        const closeQuickAddButton = this.querySelector(".product-card__close-quick-add-button");
        closeQuickAddButton?.addEventListener("click", this.closeQuickAddPanel.bind(this));

        const overlayEle = this.querySelector('.product-card__overlay');
        overlayEle?.addEventListener("click", this.closeQuickAddPanel.bind(this));

        // also close quick add panel when clicking outside of the product card (fix mobile ux)
        document.addEventListener("click", (event: MouseEvent) => {
            const quickAddPanelEle = this.querySelector('.product-card__quick-add-panel');
            if (quickAddPanelEle && quickAddPanelEle.classList.contains('open')) {
                const target = event.target as HTMLElement;
                if (!this.contains(target)) {
                    this.closeQuickAddPanel();
                }
            }
        });
    }

    private setupHoverEffects() {
        this.checkAndSetupHoverListeners();
        
        window.addEventListener('resize', debounce(() => {
            if (window.innerWidth <= 750) {
                this.removeHoverCard();
                this.removeHoverListeners();
            } else {
                this.checkAndSetupHoverListeners();
            }
        }, 250));
    }

    private checkAndSetupHoverListeners() {
        if (window.innerWidth <= 750) return;

        // Remove existing listeners to avoid duplicates
        this.removeHoverListeners();

        this.addEventListener('mouseenter', this.handleMouseEnter.bind(this));
        this.addEventListener('mouseleave', this.handleMouseLeave.bind(this));
        this.addEventListener('focusin', this.handleFocusIn);
        this.addEventListener('focusout', this.handleFocusOut);
    }

    private removeHoverListeners() {
        this.removeEventListener('mouseenter', this.handleMouseEnter.bind(this));
        this.removeEventListener('mouseleave', this.handleMouseLeave.bind(this));
        this.removeEventListener('focusin', this.handleFocusIn);
        this.removeEventListener('focusout', this.handleFocusOut);
    }

    private handleMouseEnter() {
        if (window.innerWidth <= 750) return;

        if (this.querySelector('.product-card__quick-add-panel.open')) return;

        this.isHoverActive = true;
        this.createHoverCard();
    }
    
    private handleMouseLeave() {
        // -- SG 20260707 Keep hover content while keyboard focus is inside the card
        // -- SG 20260708 Use :focus-visible so mouse-click focus still allows hover teardown
        if (this.hasVisibleFocusInside()) return;
        this.isHoverActive = false;
        this.removeHoverCard();
    }
    
    private createHoverCard() {
        this.removeHoverCard();

        if (!this.isHoverActive) return;

        this.classList.add('has-hover-content');

        this.hoverCardContainer = document.createElement('div');
        this.hoverCardContainer.className = 'product-card__hover-content';

        const quickAddButtons = this.querySelector('.product-card__quick-buttons.desktop');
        if (quickAddButtons) {
            this.hoverCardContainer.appendChild(quickAddButtons);
        }

        const infoSection = this.querySelector('.product-card__wrapper > .product-card__info');
        if (infoSection) {
            const infoClone = infoSection.cloneNode(true) as HTMLElement;
            this.hoverCardContainer.appendChild(infoClone);
        }

        const ratingSection = this.querySelector('.product-card__rating');
        if (ratingSection) {
            const ratingClone = ratingSection.cloneNode(true) as HTMLElement;
            this.hoverCardContainer.appendChild(ratingClone);
        }


        this.appendChild(this.hoverCardContainer);

        this.positionHoverCard();
    }
    
    private positionHoverCard() {
        if (!this.hoverCardContainer) return;

        const imgSection = this.querySelector('.product-card__img');
        const imgHeight = imgSection ? imgSection.getBoundingClientRect().height : 0;
        const cardHeight = this.getBoundingClientRect().height;
        const quickAddButtonsHeight = this.hoverCardContainer.querySelector('.product-card__quick-buttons.desktop')?.getBoundingClientRect().height || 0;
        const minHoverHeight = cardHeight - imgHeight + quickAddButtonsHeight;

        this.hoverCardContainer.style.top = `${imgHeight}px`;
        this.hoverCardContainer.style.minHeight = `${minHoverHeight}px`;
    }
    
    private removeHoverCard() {
        if (this.hoverCardContainer) {
            const quickAddButtons = this.hoverCardContainer.querySelector('.product-card__quick-buttons.desktop');
            if (quickAddButtons) {
                const imgSection = this.querySelector('.product-card__img');
                if (imgSection) {
                    imgSection.appendChild(quickAddButtons);
                }
            }

            this.hoverCardContainer.remove();
            this.hoverCardContainer = null;
            
            this.classList.remove('has-hover-content');
        }
    }

    openQuickAddPanel(event: Event) {
        event.stopPropagation();
        event.preventDefault();
        const quickAddPanelEle = this.querySelector('.product-card__quick-add-panel') as HTMLElement | null;
        if (!quickAddPanelEle || quickAddPanelEle.classList.contains('open')) return;

        this.quickAddTrigger = event.currentTarget as HTMLElement;
        quickAddPanelEle.classList.add('open');
        this.setQuickAddExpanded(true);

        const overlayEle = this.querySelector('.product-card__overlay');

        const target = event.currentTarget as HTMLElement;
        const isMobile = target.classList.contains("mobile") || target.closest('.product-card__quick-buttons.mobile') !== null;
        const template = document.querySelector("body")?.getAttribute("data-template");
        const isWishlistContext = this.closest('swymcs-custom-wishlist') !== null;

        if(isMobile && (template == "collection" || isWishlistContext)) {
            const generalOverlay = new GeneralOverlay();
            generalOverlay.applyOverlay(quickAddPanelEle);
        }

        if(window.innerWidth <=750 && (template =="collection" || isWishlistContext)){
            // show overlay and disable body scroll
            overlayEle?.classList.add('open');
            document.body.style.overflow = 'hidden';
            // -- SG 20260707 Expose the mobile quick-add panel as a modal dialog to assistive tech
            quickAddPanelEle.setAttribute('role', 'dialog');
            quickAddPanelEle.setAttribute('aria-modal', 'true');
            const cardTitle = this.querySelector('.product-card__wrapper > .product-card__info .product-card__title')?.textContent?.trim();
            if (cardTitle) quickAddPanelEle.setAttribute('aria-label', cardTitle);
        }

        // -- SG 20260703 Focus trap + move focus into quick-add panel on open
        this.installQuickAddTraps(quickAddPanelEle);
        const closeQuickAddButton = this.querySelector(".product-card__close-quick-add-button") as HTMLElement;
        closeQuickAddButton?.focus();
    }

    closeQuickAddPanel() {
        const template = document.querySelector("body")?.getAttribute("data-template");
        const quickAddPanelEle = this.querySelector('.product-card__quick-add-panel') as HTMLElement | null;
        const wasOpen = quickAddPanelEle?.classList.contains('open') ?? false;
        // -- SG 20260707 Return focus on any modal-style close (overlay/outside tap, close button, Escape),
        // not only when focus is still inside the panel -- overlay clicks move focus out of the panel.
        const overlayWasOpen = this.querySelector('.product-card__overlay')?.classList.contains('open') ?? false;
        const shouldReturnFocus = wasOpen && (overlayWasOpen || !!quickAddPanelEle?.contains(document.activeElement));

        quickAddPanelEle?.classList.remove('open');
        this.setQuickAddExpanded(false);
        this.releaseQuickAddTraps();
        // -- SG 20260707 Drop modal dialog semantics when closed
        quickAddPanelEle?.removeAttribute('role');
        quickAddPanelEle?.removeAttribute('aria-modal');
        quickAddPanelEle?.removeAttribute('aria-label');
        
        const overlayEle = this.querySelector('.product-card__overlay');
        const isWishlistContext = this.closest('swymcs-custom-wishlist') !== null;
        
        if(window.innerWidth <=750 && (template == "collection" || isWishlistContext)){
            // hide overlay and enable body scroll
            overlayEle?.classList.remove('open');
            document.body.style.overflow = '';
        }

        if (shouldReturnFocus && this.quickAddTrigger) {
            this.quickAddTrigger.focus();
        }
        this.quickAddTrigger = null;
    }

    // -- SG 20260703 Toggle aria-expanded on triggers and aria-hidden on panel
    // -- SG 20260707 Also toggle inert so closed panel controls are not focusable
    private setQuickAddExpanded(expanded: boolean) {
        this.querySelectorAll('.product-card__quick-add').forEach((trigger) => {
            trigger.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        });
        const panel = this.querySelector('.product-card__quick-add-panel');
        panel?.setAttribute('aria-hidden', expanded ? 'false' : 'true');
        if (expanded) {
            panel?.removeAttribute('inert');
        } else {
            panel?.setAttribute('inert', '');
        }
    }

    // -- SG 20260703 Escape + focus trap for quick-add panel (header-country.ts pattern)
    private installQuickAddTraps(panel: HTMLElement) {
        this.releaseQuickAddTraps();
        this.escapeHandler = (e: KeyboardEvent) => {
            if (e.key === "Escape") { e.stopPropagation(); this.closeQuickAddPanel(); }
        };
        this.focusTrapHandler = (e: KeyboardEvent) => {
            if (e.key !== "Tab") return;
            const focusable = panel.querySelectorAll<HTMLElement>(
                'a[href], button:not([disabled]), [tabindex="0"], input:not([disabled]), select:not([disabled]), textarea:not([disabled])'
            );
            if (focusable.length === 0) return;
            const first = focusable[0];
            const last = focusable[focusable.length - 1];
            if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
            else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
        };
        document.addEventListener("keydown", this.escapeHandler);
        document.addEventListener("keydown", this.focusTrapHandler);
    }

    private releaseQuickAddTraps() {
        if (this.escapeHandler) { document.removeEventListener("keydown", this.escapeHandler); this.escapeHandler = null; }
        if (this.focusTrapHandler) { document.removeEventListener("keydown", this.focusTrapHandler); this.focusTrapHandler = null; }
    }

    onAddToCartClick(event: Event) {
        event.stopPropagation();
        event.preventDefault();
        this.closeQuickAddPanel();
        const variantInput = this.querySelector('product-variants input[type="radio"]:checked') as HTMLInputElement;
        const itemKey = variantInput.value;
        const quantityInput = this.querySelector('product-quantity input[type="number"]') as HTMLInputElement;
        const quantity = Number(quantityInput.value);

        const previewCart = new PreviewCart();

        previewCart.addItem(itemKey, quantity)
        .then((data: any) => {
            if (data.status && data.status >= 400) {
                previewCart.closePreviewCart();
                this.querySelectorAll(".product-form__error-message-wrapper").forEach((ele: any) => {
                    previewCart.cartErrorHandling(ele, data.description);
                })

                const quickAddElement = this.querySelector(".product-card__quick-add.mobile") as HTMLElement;
                quickAddElement.click();
            }
            else {
                previewCart.refreshPreviewCart(data);
            }
        })
        .catch((error) => {
            console.error("error", error)
        })
    }
}

customElements.define("product-card", ProductCard);

